import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  CheckCircle2,
  Edit2,
  Eye,
  EyeOff,
  Heart,
  ImagePlus,
  Link2,
  Loader2,
  MapPin,
  PackageSearch,
  Plus,
  RotateCcw,
  Save,
  Search,
  Smartphone,
  Sparkles,
  Trash2,
  Upload,
  X
} from 'lucide-react';

import { API_URL } from '../config';
import Dropdown from './Dropdown';
import ModalShell from './ModalShell';


const CATEGORIES = [
  { value: 'laundry', label: 'Стирка' },
  { value: 'carpets', label: 'Ковры' },
  { value: 'furniture', label: 'Мебель' },
  { value: 'clothes', label: 'Одежда' }
];

const MEASUREMENTS = [
  { value: 'quantity', label: 'Количество' },
  { value: 'area', label: 'Площадь' },
  { value: 'weight', label: 'Вес' }
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Все карточки' },
  { value: 'published', label: 'Опубликованные' },
  { value: 'draft', label: 'Черновики' },
  { value: 'attention', label: 'Требуют внимания' },
  { value: 'missing_image', label: 'Без фото' },
  { value: 'missing_price', label: 'Без цены' },
  { value: 'missing_service', label: 'Без рабочей услуги' }
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const mediaUrl = (value) => value?.startsWith('/uploads/') ? `${API_URL}${value}` : value;
const categoryLabel = (value) => CATEGORIES.find((row) => row.value === value)?.label || value;
const formatPrice = (price) => {
  const amount = Number(price?.unit_price || 0);
  if (amount <= 0) return 'Цена не задана';
  const value = amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  return `${value}${price.unit_type === 'percent' ? '%' : ' ₽'}`;
};
const errorMessage = (payload, fallback) => {
  const detail = payload?.detail;
  return typeof detail === 'string' ? detail : detail?.message || fallback;
};

const request = async (path, options = {}) => {
  const response = await fetch(`${API_URL}/api/client-catalog-admin/v1${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {})
    }
  });
  const payload = response.status === 204 ? null : await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(errorMessage(payload, 'Не удалось выполнить действие'));
  return payload;
};

const inferMeasurement = (service) => {
  const unit = String(service?.unit || '').toLowerCase();
  if (unit.includes('м2') || unit.includes('м²')) return 'area';
  if (unit.includes('кг')) return 'weight';
  return 'quantity';
};

const normalizedDepartmentIds = (item) => (item?.department_ids || []).map(Number);
const isAvailableInDepartment = (item, departmentId) => {
  if (!departmentId || item?.availability_mode !== 'selected') return true;
  return normalizedDepartmentIds(item).includes(Number(departmentId));
};
const activeVariants = (item, departmentId = '') => (item?.variants || []).filter(
  (variant) => (
    variant.is_visible !== false
    && variant.service_active !== false
    && variant.service_id
    && isAvailableInDepartment(variant, departmentId)
  )
);

const primaryVariant = (item, departmentId = '') => {
  const variants = activeVariants(item, departmentId);
  return variants.find((variant) => variant.is_default) || variants[0] || null;
};

export const getCatalogReadiness = (item, departmentId = '') => {
  const unavailable = !isAvailableInDepartment(item, departmentId);
  const allVariants = activeVariants(item);
  const variants = unavailable ? allVariants : activeVariants(item, departmentId);
  const missingImage = !String(item?.image_url || '').trim();
  const missingService = variants.length === 0;
  const missingPrice = !unavailable && !missingService && !variants.some(
    (variant) => Number(variant.preview_price?.unit_price || 0) > 0
  );
  const issues = [];

  if (missingImage) issues.push('Нет фото');
  if (missingService) issues.push(departmentId && allVariants.length ? 'Нет варианта для подразделения' : 'Нет активной рабочей услуги');
  if (missingPrice) issues.push('Нет цены');

  return {
    ready: issues.length === 0,
    unavailable,
    issues,
    missingImage,
    missingService,
    missingPrice
  };
};

const normalizeSearch = (value) => String(value || '').trim().toLocaleLowerCase('ru-RU');

export const filterCatalogItems = (items, { search = '', status = 'all', category = 'all', departmentId = '' } = {}) => {
  const needle = normalizeSearch(search);
  return (items || []).filter((item) => {
    const readiness = getCatalogReadiness(item, departmentId);
    const haystack = normalizeSearch([
      item.name,
      item.caption,
      item.description,
      categoryLabel(item.category),
      ...(item.variants || []).flatMap((variant) => [variant.label, variant.service_name])
    ].join(' '));
    const matchesSearch = !needle || haystack.includes(needle);
    const matchesCategory = category === 'all' || item.category === category;
    const matchesStatus = status === 'all'
      || (status === 'published' && item.is_visible)
      || (status === 'draft' && !item.is_visible)
      || (status === 'attention' && !readiness.ready)
      || (status === 'missing_image' && readiness.missingImage)
      || (status === 'missing_price' && readiness.missingPrice)
      || (status === 'missing_service' && readiness.missingService);
    return matchesSearch && matchesCategory && matchesStatus;
  });
};

export const reorderCatalogItemIds = (items, itemId, direction, visibleItems = items) => {
  const ids = (items || []).map((item) => item.id);
  const visibleIds = (visibleItems || []).map((item) => item.id);
  const visibleIndex = visibleIds.findIndex((id) => Number(id) === Number(itemId));
  const targetVisibleIndex = visibleIndex + direction;
  if (visibleIndex < 0 || targetVisibleIndex < 0 || targetVisibleIndex >= visibleIds.length) return ids;

  const currentIndex = ids.findIndex((id) => Number(id) === Number(itemId));
  const targetIndex = ids.findIndex((id) => Number(id) === Number(visibleIds[targetVisibleIndex]));
  if (currentIndex < 0 || targetIndex < 0) return ids;
  [ids[currentIndex], ids[targetIndex]] = [ids[targetIndex], ids[currentIndex]];
  return ids;
};

const emptyForm = {
  serviceId: '',
  name: '',
  caption: '',
  description: '',
  category: 'laundry',
  measurement_mode: 'quantity',
  is_featured: false,
  availability_mode: 'all',
  department_ids: []
};

const ClientCatalogManager = ({ services = [] }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editVariant, setEditVariant] = useState(null);
  const [variantScopeForm, setVariantScopeForm] = useState({ availability_mode: 'all', department_ids: [] });
  const [previewItemId, setPreviewItemId] = useState(null);
  const [variantDrafts, setVariantDrafts] = useState({});
  const [addonDrafts, setAddonDrafts] = useState({});
  const [departments, setDepartments] = useState([]);
  const [readinessMatrix, setReadinessMatrix] = useState({ departments: [], rows: [] });
  const [previewDepartmentId, setPreviewDepartmentId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const b2cServices = useMemo(
    () => services.filter((service) => service.active !== false && ['b2c', 'all'].includes(service.client_type)),
    [services]
  );
  const baseServices = useMemo(
    () => b2cServices.filter((service) => !String(service.service_type || '').startsWith('extra_')),
    [b2cServices]
  );
  const extraServices = useMemo(
    () => b2cServices.filter((service) => String(service.service_type || '').startsWith('extra_')),
    [b2cServices]
  );
  const linkedServiceIds = useMemo(
    () => new Set(items.flatMap((item) => item.variants.map((variant) => variant.service_id))),
    [items]
  );
  const filteredItems = useMemo(
    () => filterCatalogItems(items, { search, status: statusFilter, category: categoryFilter, departmentId: previewDepartmentId }),
    [items, search, statusFilter, categoryFilter, previewDepartmentId]
  );
  const summary = useMemo(() => ({
    total: items.length,
    published: items.filter((item) => item.is_visible).length,
    draft: items.filter((item) => !item.is_visible).length,
    attention: items.filter((item) => !getCatalogReadiness(item, previewDepartmentId).ready).length
  }), [items, previewDepartmentId]);
  const previewItem = items.find((item) => item.id === previewItemId) || null;
  const selectedDepartment = departments.find(
    (row) => Number(row.id) === Number(previewDepartmentId)
  );
  const priceContextLabel = selectedDepartment?.name || 'Базовая цена';
  const filtersActive = Boolean(search.trim()) || statusFilter !== 'all' || categoryFilter !== 'all';

  const load = async (departmentId = previewDepartmentId, showLoader = true) => {
    if (showLoader) setLoading(true);
    setError('');
    try {
      const [catalog, readinessPayload] = await Promise.all([
        request(`/catalog${departmentId ? `?department_id=${encodeURIComponent(departmentId)}` : ''}`),
        request('/catalog-readiness')
      ]);
      setItems(catalog || []);
      setReadinessMatrix(readinessPayload || { departments: [], rows: [] });
      setDepartments(readinessPayload?.departments || []);
      setAddonDrafts(Object.fromEntries((catalog || []).map((item) => [
        item.id,
        item.addons.map((addon) => addon.addon_service_id)
      ])));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, action, successText) => {
    setBusy(key);
    setError('');
    setNotice('');
    try {
      await action();
      setNotice(successText);
      await load(previewDepartmentId, false);
      return true;
    } catch (actionError) {
      setError(actionError.message);
      return false;
    } finally {
      setBusy('');
    }
  };

  const chooseCreateService = (value) => {
    const service = baseServices.find((row) => row.id === Number(value));
    setCreateForm((current) => ({
      ...current,
      serviceId: value,
      name: service?.name || current.name,
      caption: service?.description || current.caption,
      description: service?.description || current.description,
      measurement_mode: service ? inferMeasurement(service) : current.measurement_mode
    }));
  };

  const createCard = () => {
    const service = baseServices.find((row) => row.id === Number(createForm.serviceId));
    if (!service || !createForm.name.trim()) {
      setError('Выберите рабочую услугу и укажите название карточки');
      return;
    }
    if (createForm.availability_mode === 'selected' && !createForm.department_ids.length) {
      setError('Выберите хотя бы одно подразделение');
      return;
    }
    const lastSortOrder = items.reduce((maximum, item) => Math.max(maximum, Number(item.sort_order || 0)), -10);
    run('create', async () => {
      const card = await request('/catalog', {
        method: 'POST',
        body: JSON.stringify({
          slug: `service-${service.id}-${Date.now().toString(36)}`,
          name: createForm.name.trim(),
          caption: createForm.caption.trim() || null,
          description: createForm.description.trim() || null,
          category: createForm.category,
          measurement_mode: createForm.measurement_mode,
          is_visible: false,
          is_featured: Boolean(createForm.is_featured),
          availability_mode: createForm.availability_mode,
          department_ids: createForm.availability_mode === 'selected' ? createForm.department_ids : [],
          sort_order: lastSortOrder + 10
        })
      });
      await request(`/catalog/${card.id}/variants`, {
        method: 'PUT',
        body: JSON.stringify([{
          service_id: service.id,
          code: `service-${service.id}`,
          label: service.name,
          caption: service.description || null,
          is_default: true,
          is_visible: true,
          availability_mode: createForm.availability_mode,
          department_ids: createForm.availability_mode === 'selected' ? createForm.department_ids : [],
          sort_order: 0
        }])
      });
      setShowCreate(false);
      setCreateForm(emptyForm);
    }, 'Черновик создан. Загрузите фото и опубликуйте карточку.');
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || '',
      caption: item.caption || '',
      description: item.description || '',
      category: item.category || 'laundry',
      measurement_mode: item.measurement_mode || 'quantity',
      is_featured: Boolean(item.is_featured),
      availability_mode: item.availability_mode || 'all',
      department_ids: item.department_ids || []
    });
  };

  const saveEdit = () => {
    if (!editItem || !editForm.name.trim()) return;
    if (editForm.availability_mode === 'selected' && !editForm.department_ids.length) {
      setError('Выберите хотя бы одно подразделение');
      return;
    }
    run(`edit-${editItem.id}`, async () => {
      await request(`/catalog/${editItem.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...editForm,
          name: editForm.name.trim(),
          caption: editForm.caption.trim() || null,
          description: editForm.description.trim() || null,
          department_ids: editForm.availability_mode === 'selected' ? editForm.department_ids : []
        })
      });
      setEditItem(null);
    }, 'Карточка сохранена');
  };

  const patchCard = (item, data, successText) => run(
    `item-${item.id}`,
    () => request(`/catalog/${item.id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    successText
  );

  const togglePublication = (item) => {
    if (!item.is_visible) {
      const readiness = getCatalogReadiness(item, previewDepartmentId);
      if (!readiness.ready) {
        setNotice('');
        setError(`Карточка пока не готова: ${readiness.issues.join(', ').toLocaleLowerCase('ru-RU')}`);
        return;
      }
    }
    patchCard(
      item,
      { is_visible: !item.is_visible },
      item.is_visible ? 'Карточка скрыта' : 'Карточка опубликована'
    );
  };

  const moveCard = (item, direction) => {
    const itemIds = reorderCatalogItemIds(items, item.id, direction, filteredItems);
    if (itemIds.every((id, index) => id === items[index]?.id)) return;
    run(
      'order',
      () => request('/catalog/order', {
        method: 'PUT',
        body: JSON.stringify({ item_ids: itemIds })
      }),
      'Порядок карточек сохранён'
    );
  };

  const uploadImage = (item, kind, targetId, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    const suffix = kind === 'card' ? 'image' : `${kind}/${targetId}/image`;
    run(
      `upload-${kind}-${targetId || item.id}`,
      () => request(`/catalog/${item.id}/${suffix}`, { method: 'POST', body: form }),
      'Изображение обработано и сохранено'
    );
  };

  const saveVariants = (item, nextServiceId = null, removeVariantId = null) => {
    const variants = item.variants
      .filter((variant) => variant.id !== removeVariantId)
      .map((variant, index) => ({
        service_id: variant.service_id,
        code: variant.code,
        label: variant.label,
        caption: variant.caption,
        image_url: variant.image_url,
        thumbnail_url: variant.thumbnail_url,
        metadata: variant.metadata || {},
        is_default: index === 0,
        is_visible: variant.is_visible,
        availability_mode: variant.availability_mode || 'all',
        department_ids: variant.availability_mode === 'selected' ? (variant.department_ids || []) : [],
        sort_order: index * 10
      }));
    if (nextServiceId) {
      const service = baseServices.find((row) => row.id === Number(nextServiceId));
      if (service && !variants.some((variant) => variant.service_id === service.id)) {
        variants.push({
          service_id: service.id,
          code: `service-${service.id}`,
          label: service.name,
          caption: service.description || null,
          is_default: variants.length === 0,
          is_visible: true,
          availability_mode: item.availability_mode || 'all',
          department_ids: item.availability_mode === 'selected' ? (item.department_ids || []) : [],
          sort_order: variants.length * 10
        });
      }
    }
    if (!variants.length) {
      setError('У карточки должен остаться хотя бы один вариант');
      return;
    }
    run(
      `variants-${item.id}`,
      () => request(`/catalog/${item.id}/variants`, { method: 'PUT', body: JSON.stringify(variants) }),
      'Варианты карточки обновлены'
    );
  };

  const openVariantScope = (item, variant) => {
    setEditVariant({ item, variant });
    setVariantScopeForm({
      availability_mode: variant.availability_mode || 'all',
      department_ids: variant.department_ids || []
    });
  };

  const saveVariantScope = () => {
    if (!editVariant) return;
    if (variantScopeForm.availability_mode === 'selected' && !variantScopeForm.department_ids.length) {
      setError('Выберите хотя бы одно подразделение для варианта');
      return;
    }
    const { item, variant: targetVariant } = editVariant;
    const payload = item.variants.map((variant, index) => ({
      service_id: variant.service_id,
      code: variant.code,
      label: variant.label,
      caption: variant.caption,
      image_url: variant.image_url,
      thumbnail_url: variant.thumbnail_url,
      metadata: variant.metadata || {},
      is_default: Boolean(variant.is_default),
      is_visible: variant.is_visible !== false,
      availability_mode: variant.id === targetVariant.id ? variantScopeForm.availability_mode : (variant.availability_mode || 'all'),
      department_ids: variant.id === targetVariant.id
        ? (variantScopeForm.availability_mode === 'selected' ? variantScopeForm.department_ids : [])
        : (variant.availability_mode === 'selected' ? (variant.department_ids || []) : []),
      sort_order: index * 10
    }));
    run(
      `variant-scope-${targetVariant.id}`,
      () => request(`/catalog/${item.id}/variants`, { method: 'PUT', body: JSON.stringify(payload) }),
      'Доступность варианта сохранена'
    ).then((saved) => {
      if (saved) setEditVariant(null);
    });
  };

  const saveAddons = (item) => {
    const selectedIds = addonDrafts[item.id] || [];
    const payload = selectedIds.map((id, index) => {
      const current = item.addons.find((row) => row.addon_service_id === id);
      const service = extraServices.find((row) => row.id === id);
      return {
        addon_service_id: id,
        name_override: current?.name_override || service?.name || null,
        caption: current?.caption || service?.description || null,
        description: current?.description || service?.description || null,
        image_url: current?.image_url || null,
        thumbnail_url: current?.thumbnail_url || null,
        tone: current?.tone || 'sky',
        is_visible: true,
        sort_order: index * 10
      };
    });
    run(
      `addons-${item.id}`,
      () => request(`/catalog/${item.id}/addons`, { method: 'PUT', body: JSON.stringify(payload) }),
      'Допуслуги обновлены'
    );
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setCategoryFilter('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl bg-white p-12 text-gray-500 shadow-sm dark:bg-gray-900">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Загружаем витрину…
      </div>
    );
  }

  const availableBaseServices = baseServices.filter((service) => !linkedServiceIds.has(service.id));
  const departmentOptions = [
    { value: '', label: 'Базовая цена', variant: 'neutral' },
    ...departments.map((row) => ({ value: row.id, label: row.name }))
  ];
  const categoryOptions = [
    { value: 'all', label: 'Все разделы', variant: 'neutral' },
    ...CATEGORIES
  ];

  return (
    <div className="space-y-3">
      <section className="overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-600 to-cyan-500 p-4 text-white shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/20">
              <Smartphone className="h-6 w-6" />
            </span>
            <div>
              <h2 className="font-bold">Витрина Постирано.Ру</h2>
              <p className="mt-1 max-w-2xl text-sm text-blue-50">
                Рабочие услуги и цены остаются источником расчёта. Здесь вы управляете фото,
                понятными названиями, вариантами и порядком показа клиенту.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-blue-700"
          >
            <Plus className="h-4 w-4" />
            Новая карточка
          </button>
        </div>
      </section>

      {(error || notice) && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'}`}>
          {error || notice}
        </div>
      )}

      <section className="rounded-2xl bg-white p-3 shadow-sm dark:bg-gray-900">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <SummaryButton label="Всего" value={summary.total} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
          <SummaryButton label="Опубликовано" value={summary.published} tone="emerald" active={statusFilter === 'published'} onClick={() => setStatusFilter('published')} />
          <SummaryButton label="Черновики" value={summary.draft} tone="gray" active={statusFilter === 'draft'} onClick={() => setStatusFilter('draft')} />
          <SummaryButton label="Требуют внимания" value={summary.attention} tone="amber" active={statusFilter === 'attention'} onClick={() => setStatusFilter('attention')} />
        </div>

        <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-[minmax(240px,1fr)_220px_200px_220px_auto]">
          <label className="flex h-[50px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 dark:border-gray-700 dark:bg-gray-900 dark:focus-within:ring-blue-950">
            <Search className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Найти карточку или услугу"
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Очистить поиск">
                <X className="h-4 w-4" />
              </button>
            )}
          </label>
          <Dropdown
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
            label="Состояние"
            buttonClassName="h-[50px]"
          />
          <Dropdown
            value={categoryFilter}
            options={categoryOptions}
            onChange={setCategoryFilter}
            label="Раздел"
            buttonClassName="h-[50px]"
          />
          <Dropdown
            value={previewDepartmentId}
            options={departmentOptions}
            onChange={(value) => {
              setPreviewDepartmentId(value);
              load(value);
            }}
            label="Цены подразделения"
            buttonClassName="h-[50px]"
          />
          {filtersActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-[50px] items-center justify-center gap-2 rounded-xl border border-gray-200 px-3 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <RotateCcw className="h-4 w-4" />
              Сбросить
            </button>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-500">
          <span>Показано: {filteredItems.length} из {items.length}</span>
          <span>Цены в карточках: <strong className="font-semibold text-gray-700 dark:text-gray-200">{priceContextLabel}</strong></span>
        </div>
      </section>

      <CatalogReadinessMatrix
        matrix={readinessMatrix}
        selectedDepartmentId={previewDepartmentId}
        onSelectDepartment={(departmentId) => {
          setPreviewDepartmentId(departmentId);
          load(departmentId);
        }}
      />

      <div className="grid gap-3 xl:grid-cols-2">
        {filteredItems.map((item) => {
          const readiness = getCatalogReadiness(item, previewDepartmentId);
          const variant = primaryVariant(item, previewDepartmentId);
          const orderIndex = filteredItems.findIndex((row) => row.id === item.id);
          return (
            <article key={item.id} className={`overflow-hidden rounded-2xl border bg-white shadow-sm dark:bg-gray-900 ${readiness.ready ? 'border-gray-100 dark:border-gray-800' : 'border-amber-200 dark:border-amber-900'}`}>
              <div className="flex gap-3 p-3">
                <label className="relative flex h-28 w-28 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-800">
                  {item.image_url
                    ? <img src={mediaUrl(item.thumbnail_url || item.image_url)} alt={item.name} className="h-full w-full object-cover" />
                    : <ImagePlus className="h-8 w-8" />}
                  <input type="file" accept="image/*" className="hidden" onChange={(event) => uploadImage(item, 'card', null, event.target.files?.[0])} />
                  <span className="absolute inset-x-1 bottom-1 rounded-lg bg-black/65 py-1 text-center text-[10px] text-white">
                    <Upload className="mr-1 inline h-3 w-3" />
                    {item.image_url ? 'Заменить' : 'Добавить фото'}
                  </span>
                </label>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate font-bold text-gray-900 dark:text-white">{item.name}</h3>
                      <p className="mt-1 line-clamp-2 text-xs text-gray-500">{item.caption || 'Добавьте короткое понятное описание'}</p>
                    </div>
                    <button
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => togglePublication(item)}
                      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold disabled:opacity-50 ${item.is_visible ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}
                    >
                      {busy === `item-${item.id}`
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : item.is_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                      {item.is_visible ? 'В приложении' : 'Черновик'}
                    </button>
                  </div>

                  <div className="mt-2 flex items-end justify-between gap-2">
                    <div>
                      <div className={`text-sm font-bold ${Number(variant?.preview_price?.unit_price || 0) > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {formatPrice(variant?.preview_price)}
                        {variant?.service_unit && Number(variant?.preview_price?.unit_price || 0) > 0 && (
                          <span className="ml-1 text-xs font-medium text-gray-500">/ {variant.service_unit}</span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-400">{priceContextLabel}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={Boolean(busy) || orderIndex === 0}
                        onClick={() => moveCard(item, -1)}
                        className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 dark:border-gray-700 dark:hover:bg-gray-800"
                        aria-label={`Поднять «${item.name}» выше`}
                        title="Поднять выше"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={Boolean(busy) || orderIndex === filteredItems.length - 1}
                        onClick={() => moveCard(item, 1)}
                        className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 dark:border-gray-700 dark:hover:bg-gray-800"
                        aria-label={`Опустить «${item.name}» ниже`}
                        title="Опустить ниже"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 border-t border-gray-100 px-3 py-2.5 dark:border-gray-800">
                <span className="rounded-lg bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">{categoryLabel(item.category)}</span>
                {item.is_featured && <span className="rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-300"><Sparkles className="mr-1 inline h-3 w-3" />Популярное</span>}
                <span className="rounded-lg bg-blue-50 px-2 py-1 text-xs text-blue-700 dark:bg-blue-950/30 dark:text-blue-300"><MapPin className="mr-1 inline h-3 w-3" />{item.availability_mode === 'selected' ? `${item.department_ids?.length || 0} подр.` : 'Все подразделения'}</span>
                {readiness.unavailable && <span className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">Не показывается: {priceContextLabel}</span>}
                {readiness.ready ? (
                  <span className="rounded-lg bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"><CheckCircle2 className="mr-1 inline h-3 w-3" />Готово</span>
                ) : readiness.issues.map((issue) => (
                  <span key={issue} className="rounded-lg bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 dark:bg-amber-950/30 dark:text-amber-300"><AlertTriangle className="mr-1 inline h-3 w-3" />{issue}</span>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 border-t border-gray-100 px-3 py-2.5 dark:border-gray-800">
                <button type="button" onClick={() => setPreviewItemId(item.id)} className="rounded-lg border border-blue-200 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950/30"><Smartphone className="mr-1 inline h-3.5 w-3.5" />Как видит клиент</button>
                <button type="button" onClick={() => openEdit(item)} className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"><Edit2 className="mr-1 inline h-3.5 w-3.5" />Изменить</button>
                {!item.is_visible && <button type="button" onClick={() => run(`delete-${item.id}`, () => request(`/catalog/${item.id}`, { method: 'DELETE' }), 'Черновик удалён')} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/30"><Trash2 className="mr-1 inline h-3.5 w-3.5" />Удалить</button>}
              </div>

              <details className="border-t border-gray-100 p-3 dark:border-gray-800">
                <summary className="cursor-pointer text-sm font-semibold text-gray-800 dark:text-gray-100">
                  <Link2 className="mr-2 inline h-4 w-4 text-blue-600" />
                  Варианты и рабочие цены ({item.variants.length})
                </summary>
                <div className="mt-3 space-y-2">
                  {item.variants.map((row) => (
                    <div key={row.id} className="flex items-center gap-2 rounded-xl bg-gray-50 p-2 dark:bg-gray-800">
                      <label className="flex h-11 w-11 cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-white text-gray-400 dark:bg-gray-900">
                        {row.image_url ? <img src={mediaUrl(row.thumbnail_url || row.image_url)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-4 w-4" />}
                        <input type="file" accept="image/*" className="hidden" onChange={(event) => uploadImage(item, 'variants', row.id, event.target.files?.[0])} />
                      </label>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">{row.label}</div>
                        <div className="truncate text-xs text-gray-500">{row.service_name}</div>
                        <div className="mt-0.5 text-xs font-semibold text-blue-600 dark:text-blue-400">
                          {formatPrice(row.preview_price)}{row.service_unit && Number(row.preview_price?.unit_price || 0) > 0 ? ` / ${row.service_unit}` : ''}
                        </div>
                      </div>
                      <button type="button" onClick={() => openVariantScope(item, row)} className="rounded-lg border border-blue-200 px-2 py-1 text-[10px] font-medium text-blue-700 dark:border-blue-800 dark:text-blue-300"><MapPin className="mr-1 inline h-3 w-3" />{row.availability_mode === 'selected' ? `${row.department_ids?.length || 0} подр.` : 'Везде'}</button>
                      {row.service_active === false && <span className="rounded-lg bg-red-50 px-2 py-1 text-[10px] font-medium text-red-600 dark:bg-red-950/30 dark:text-red-300">Отключена</span>}
                      {item.variants.length > 1 && <button type="button" onClick={() => saveVariants(item, null, row.id)} className="rounded-lg p-2 text-red-500" aria-label={`Удалить вариант «${row.label}»`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <select value={variantDrafts[item.id] || ''} onChange={(event) => setVariantDrafts((current) => ({ ...current, [item.id]: event.target.value }))} className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                      <option value="">Добавить рабочий вариант…</option>
                      {baseServices.filter((service) => !linkedServiceIds.has(service.id)).map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
                    </select>
                    <button type="button" disabled={!variantDrafts[item.id] || Boolean(busy)} onClick={() => saveVariants(item, variantDrafts[item.id])} className="rounded-xl bg-blue-600 px-3 text-white disabled:opacity-40" aria-label="Добавить вариант"><Plus className="h-4 w-4" /></button>
                  </div>
                </div>
              </details>

              <details className="border-t border-gray-100 p-3 dark:border-gray-800">
                <summary className="cursor-pointer text-sm font-semibold text-gray-800 dark:text-gray-100">
                  <Sparkles className="mr-2 inline h-4 w-4 text-violet-600" />
                  Допуслуги ({item.addons.length})
                </summary>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {extraServices.map((service) => {
                    const checked = (addonDrafts[item.id] || []).includes(service.id);
                    return (
                      <label key={service.id} className={`flex cursor-pointer items-center gap-2 rounded-xl border p-2 text-sm ${checked ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30' : 'border-gray-200 dark:border-gray-700'}`}>
                        <input type="checkbox" checked={checked} onChange={() => setAddonDrafts((current) => ({ ...current, [item.id]: checked ? (current[item.id] || []).filter((id) => id !== service.id) : [...(current[item.id] || []), service.id] }))} />
                        <span className="min-w-0 flex-1 truncate">{service.name}</span>
                        {checked && <Check className="h-4 w-4 text-blue-600" />}
                      </label>
                    );
                  })}
                </div>
                <button type="button" disabled={Boolean(busy)} onClick={() => saveAddons(item)} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" />Сохранить допуслуги</button>
                {item.addons.length > 0 && (
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {item.addons.map((addon) => (
                      <label key={addon.id} className="flex cursor-pointer items-center gap-2 rounded-xl bg-gray-50 p-2 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        {addon.image_url
                          ? <img src={mediaUrl(addon.thumbnail_url || addon.image_url)} alt="" className="h-10 w-10 rounded-lg object-cover" />
                          : <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white dark:bg-gray-900"><ImagePlus className="h-4 w-4" /></span>}
                        <span className="min-w-0 flex-1">{addon.name_override || addon.service_name}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(event) => uploadImage(item, 'addons', addon.id, event.target.files?.[0])} />
                        <Upload className="h-4 w-4" />
                      </label>
                    ))}
                  </div>
                )}
              </details>
            </article>
          );
        })}
      </div>

      {!items.length && (
        <div className="rounded-2xl bg-white p-8 text-center text-sm text-gray-500 shadow-sm dark:bg-gray-900">
          <PackageSearch className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          Витрина пуста. Создайте первую карточку — рабочие услуги сами клиентам не показываются.
        </div>
      )}
      {items.length > 0 && !filteredItems.length && (
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
          <PackageSearch className="mx-auto h-8 w-8 text-gray-400" />
          <h3 className="mt-3 font-semibold text-gray-900 dark:text-white">Карточки не найдены</h3>
          <p className="mt-1 text-sm text-gray-500">Измените запрос или сбросьте фильтры.</p>
          <button type="button" onClick={resetFilters} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white"><RotateCcw className="h-4 w-4" />Сбросить фильтры</button>
        </div>
      )}

      <CatalogFormModal open={showCreate} title="Новая карточка" form={createForm} setForm={setCreateForm} services={availableBaseServices} departments={departments} onServiceChange={chooseCreateService} onClose={() => setShowCreate(false)} onSave={createCard} busy={busy === 'create'} showService />
      <CatalogFormModal open={Boolean(editItem)} title="Карточка витрины" form={editForm} setForm={setEditForm} departments={departments} onClose={() => setEditItem(null)} onSave={saveEdit} busy={busy === `edit-${editItem?.id}`} />
      <ClientCatalogPreviewModal item={previewItem} departmentId={previewDepartmentId} departmentLabel={priceContextLabel} onClose={() => setPreviewItemId(null)} />
      <VariantScopeModal open={Boolean(editVariant)} variant={editVariant?.variant} form={variantScopeForm} setForm={setVariantScopeForm} departments={departments} busy={busy === `variant-scope-${editVariant?.variant?.id}`} onClose={() => setEditVariant(null)} onSave={saveVariantScope} />
    </div>
  );
};

const SummaryButton = ({ label, value, tone = 'blue', active, onClick }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
  };
  return (
    <button type="button" onClick={onClick} className={`rounded-xl border p-3 text-left transition ${active ? 'border-blue-400 ring-2 ring-blue-100 dark:border-blue-600 dark:ring-blue-950' : 'border-transparent hover:border-gray-200 dark:hover:border-gray-700'} ${tones[tone]}`}>
      <span className="block text-xs font-medium opacity-80">{label}</span>
      <strong className="mt-1 block text-xl leading-none">{value}</strong>
    </button>
  );
};

const MATRIX_STATE_STYLES = {
  ready: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200',
  hidden: 'border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
  draft: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200',
  attention: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200'
};

const CatalogReadinessMatrix = ({ matrix, selectedDepartmentId, onSelectDepartment }) => {
  const departments = matrix?.departments || [];
  const rows = matrix?.rows || [];
  return (
    <details className="overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-gray-900">
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
        <MapPin className="mr-2 inline h-4 w-4 text-blue-600" />
        Готовность по подразделениям
        <span className="ml-2 text-xs font-normal text-gray-500">{rows.length} карточек · {departments.length} подразделений</span>
      </summary>
      <div className="overflow-x-auto border-t border-gray-100 dark:border-gray-800">
        {departments.length && rows.length ? (
          <table className="min-w-full border-collapse text-left text-xs">
            <thead className="bg-gray-50 text-gray-500 dark:bg-gray-800/70 dark:text-gray-300">
              <tr>
                <th className="sticky left-0 z-10 min-w-44 bg-gray-50 px-3 py-2 font-semibold dark:bg-gray-800">Карточка</th>
                {departments.map((department) => (
                  <th key={department.id} className="min-w-40 px-2 py-2 font-semibold">
                    <button type="button" onClick={() => onSelectDepartment(String(department.id))} className={`w-full rounded-lg px-2 py-1.5 text-left ${Number(selectedDepartmentId) === Number(department.id) ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{department.name}</button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((row) => (
                <tr key={row.item_id}>
                  <th className="sticky left-0 z-10 max-w-56 bg-white px-3 py-2 font-medium text-gray-800 dark:bg-gray-900 dark:text-gray-100"><span className="block truncate">{row.name}</span></th>
                  {(row.cells || []).map((cell) => (
                    <td key={cell.department_id} className="px-2 py-2 align-top">
                      <button type="button" onClick={() => onSelectDepartment(String(cell.department_id))} title={(cell.issues || []).join(', ') || cell.label} className={`w-full rounded-xl border px-2 py-1.5 text-left ${MATRIX_STATE_STYLES[cell.state] || MATRIX_STATE_STYLES.hidden}`}>
                        <span className="block font-semibold">{cell.label}</span>
                        {Number(cell.price || 0) > 0 && <span className="mt-0.5 block text-[10px] opacity-80">{formatPrice({ unit_price: cell.price })}{cell.unit ? ` / ${cell.unit}` : ''}</span>}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="p-4 text-xs text-gray-500">Матрица появится после создания карточек и активных B2C-подразделений.</p>
        )}
      </div>
    </details>
  );
};

const ClientCatalogPreviewModal = ({ item, departmentId, departmentLabel, onClose }) => {
  const variant = primaryVariant(item, departmentId);
  const readiness = getCatalogReadiness(item, departmentId);
  return (
    <ModalShell open={Boolean(item)} onClose={onClose} variant="window" panelClassName="max-h-[92vh] max-w-md overflow-y-auto p-0">
      {item && (
        <>
          <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Предпросмотр</div>
              <h3 className="font-bold text-gray-900 dark:text-white">Как видит клиент</h3>
            </div>
            <button type="button" onClick={onClose} className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
          </header>

          {!readiness.ready && (
            <div className="m-4 mb-0 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <AlertTriangle className="mr-1 inline h-4 w-4" />
              До публикации: {readiness.issues.join(', ').toLocaleLowerCase('ru-RU')}.
            </div>
          )}
          {readiness.unavailable && (
            <div className="m-4 mb-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
              Карточка скрыта для подразделения «{departmentLabel}» и клиент её не увидит.
            </div>
          )}

          <div className="bg-[#f2f8fb] p-4 dark:bg-slate-950">
            <div className="overflow-hidden rounded-[24px] border border-white bg-white shadow-[0_16px_42px_rgba(55,112,145,0.14)] dark:border-gray-800 dark:bg-gray-900">
              <div className="relative aspect-[16/10] bg-gradient-to-br from-sky-50 to-cyan-100 dark:from-slate-900 dark:to-slate-800">
                {item.image_url
                  ? <img src={mediaUrl(item.image_url)} alt={item.name} className="h-full w-full object-cover" />
                  : <div className="flex h-full items-center justify-center text-sky-300"><ImagePlus className="h-16 w-16" /></div>}
                <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-sky-700 shadow-sm backdrop-blur dark:bg-gray-900/90 dark:text-sky-300">{categoryLabel(item.category)}</span>
                <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-gray-500 shadow-sm backdrop-blur dark:bg-gray-900/90"><Heart className="h-4 w-4" /></span>
              </div>
              <div className="p-4">
                {item.is_featured && <span className="mb-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-600"><Sparkles className="h-3.5 w-3.5" />Чаще выбирают</span>}
                <h4 className="text-lg font-bold text-slate-900 dark:text-white">{item.name}</h4>
                <p className="mt-1 text-sm leading-5 text-slate-500">{item.caption || item.description || 'Описание услуги появится здесь'}</p>
                <div className="mt-4 flex items-end justify-between gap-3">
                  <div>
                    <span className="block text-xs text-slate-400">от</span>
                    <strong className="text-xl text-sky-700 dark:text-sky-300">{formatPrice(variant?.preview_price)}</strong>
                    {variant?.service_unit && Number(variant?.preview_price?.unit_price || 0) > 0 && <span className="ml-1 text-xs text-slate-400">/ {variant.service_unit}</span>}
                  </div>
                  <button type="button" disabled className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-sky-600 to-cyan-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm"><Plus className="h-4 w-4" />{item.measurement_mode === 'quantity' ? 'Добавить' : 'Указать параметры'}</button>
                </div>

                {(item.variants || []).length > 1 && (
                  <div className="mt-4 border-t border-slate-100 pt-3 dark:border-gray-800">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Варианты</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {activeVariants(item, departmentId).map((row) => (
                        <span key={row.id} className={`rounded-xl border px-3 py-2 text-xs ${row.id === variant?.id ? 'border-sky-400 bg-sky-50 font-semibold text-sky-700 dark:border-sky-700 dark:bg-sky-950/30 dark:text-sky-300' : 'border-slate-200 text-slate-600 dark:border-gray-700 dark:text-gray-300'}`}>
                          {row.label} · {formatPrice(row.preview_price)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="mt-3 text-center text-xs text-slate-500">Цены: {departmentLabel}. Кнопки в предпросмотре не оформляют заказ.</p>
          </div>
        </>
      )}
    </ModalShell>
  );
};

const DepartmentScopeFields = ({ form, setForm, departments, subject = 'Карточка' }) => {
  const selectedIds = (form.department_ids || []).map(Number);
  const toggleDepartment = (departmentId) => {
    const normalizedId = Number(departmentId);
    setForm((current) => ({
      ...current,
      department_ids: selectedIds.includes(normalizedId)
        ? selectedIds.filter((id) => id !== normalizedId)
        : [...selectedIds, normalizedId]
    }));
  };
  return (
    <div className="rounded-2xl border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-200"><MapPin className="h-4 w-4 text-blue-600" />Где показывать</div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button type="button" onClick={() => setForm((current) => ({ ...current, availability_mode: 'all', department_ids: [] }))} className={`rounded-xl border px-3 py-2 text-xs font-medium ${form.availability_mode !== 'selected' ? 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300' : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300'}`}>Во всех</button>
        <button type="button" onClick={() => setForm((current) => ({ ...current, availability_mode: 'selected' }))} className={`rounded-xl border px-3 py-2 text-xs font-medium ${form.availability_mode === 'selected' ? 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300' : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300'}`}>Только выбранные</button>
      </div>
      {form.availability_mode === 'selected' && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {departments.map((department) => {
            const checked = selectedIds.includes(Number(department.id));
            return (
              <label key={department.id} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs ${checked ? 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200' : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300'}`}>
                <input type="checkbox" checked={checked} onChange={() => toggleDepartment(department.id)} />
                <span className="min-w-0 flex-1 truncate">{department.name}</span>
                {checked && <Check className="h-3.5 w-3.5 text-blue-600" />}
              </label>
            );
          })}
          {!departments.length && <p className="text-xs text-amber-600">Нет активных B2C-подразделений.</p>}
        </div>
      )}
      <p className="mt-2 text-[11px] text-gray-500">{subject} попадёт в каталог только выбранных городов. Цена продолжит браться из рабочего прайса подразделения.</p>
    </div>
  );
};

const VariantScopeModal = ({ open, variant, form, setForm, departments, busy, onClose, onSave }) => (
  <ModalShell open={open} onClose={onClose} variant="window" panelClassName="max-w-lg overflow-hidden p-0">
    <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
      <div><div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Доступность варианта</div><h3 className="font-bold text-gray-900 dark:text-white">{variant?.label || 'Вариант услуги'}</h3></div>
      <button type="button" onClick={onClose} className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
    </header>
    <div className="space-y-3 p-4">
      <DepartmentScopeFields form={form} setForm={setForm} departments={departments} subject="Вариант" />
      <button type="button" disabled={busy || (form.availability_mode === 'selected' && !form.department_ids.length)} onClick={onSave} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Сохранить доступность</button>
    </div>
  </ModalShell>
);

const CatalogFormModal = ({ open, title, form, setForm, services = [], departments = [], onServiceChange, onClose, onSave, busy, showService = false }) => (
  <ModalShell open={open} onClose={onClose} variant="window" panelClassName="max-h-[90vh] max-w-lg overflow-y-auto p-0">
    <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
      <div><div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Постирано.Ру</div><h3 className="font-bold text-gray-900 dark:text-white">{title}</h3></div>
      <button type="button" onClick={onClose} className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
    </header>
    <div className="space-y-3 p-4">
      {showService && (
        <label className="block text-xs font-medium text-gray-500">
          Рабочая услуга
          <select value={form.serviceId} onChange={(event) => onServiceChange(event.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white">
            <option value="">Выберите…</option>
            {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
          </select>
        </label>
      )}
      <label className="block text-xs font-medium text-gray-500">Название для клиента<input value={form.name} maxLength="160" onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" /></label>
      <label className="block text-xs font-medium text-gray-500">Короткое описание<input value={form.caption} maxLength="240" onChange={(event) => setForm((current) => ({ ...current, caption: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" /></label>
      <label className="block text-xs font-medium text-gray-500">Подробное описание<textarea value={form.description} maxLength="3000" rows="3" onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} className="mt-1 w-full resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" /></label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs font-medium text-gray-500">Раздел<select value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white">{CATEGORIES.map((row) => <option key={row.value} value={row.value}>{row.label}</option>)}</select></label>
        <label className="block text-xs font-medium text-gray-500">Способ расчёта<select value={form.measurement_mode} onChange={(event) => setForm((current) => ({ ...current, measurement_mode: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white">{MEASUREMENTS.map((row) => <option key={row.value} value={row.value}>{row.label}</option>)}</select></label>
      </div>
      <DepartmentScopeFields form={form} setForm={setForm} departments={departments} />
      <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200"><input type="checkbox" checked={Boolean(form.is_featured)} onChange={(event) => setForm((current) => ({ ...current, is_featured: event.target.checked }))} />Показывать как популярное</label>
      <button type="button" disabled={busy || !form.name?.trim() || (showService && !form.serviceId) || (form.availability_mode === 'selected' && !form.department_ids.length)} onClick={onSave} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Сохранить</button>
    </div>
  </ModalShell>
);

export default ClientCatalogManager;
